import React, { useContext, useState } from 'react';
import { ProviderField } from '../../../../models/provider';
import { ProviderInputState } from '../molecules/ProviderInputs/Input/Input';
import { ProviderInputValidator } from '../molecules/ProviderInputs/ProviderInputValidator';

export const calculateProviderInputs = (
  providerFields: ProviderField[],
  fixedValue?: string
): ProviderInputField => {
  const providerInputs: ProviderInputField = {};

  providerFields.forEach((providerField, index) => {
    const overwrites = {
      value: '',
      disabled: false,
      isValid: false,
    };

    if (index === 0 && fixedValue) {
      // If an input contains fixed value, we need to first check it's
      // validity before assigning it to `value` and making input `disabled`.

      const isValidByRules = new ProviderInputValidator(providerField).validate(fixedValue).onType
        ?.isValid;

      if (isValidByRules) {
        overwrites.value = fixedValue;
        overwrites.disabled = true;
        overwrites.isValid = true;
      }
    }

    if (providerField.value) {
      // e.g. In case of supplemental information, one of provider fields already has value.
      overwrites.value = providerField.value;
      overwrites.disabled = true;
      overwrites.isValid = true;
    }

    providerInputs[providerField.name] = {
      ...overwrites,
      errorMessage: '',
      providerField,
      showCheckIcon: false,
    };
  });

  return providerInputs;
};

export type ProviderInputField = {
  [inputName: string]: ProviderInputState;
};

export type OnProviderInputChange = ({
  providerInputName,
  value,
}: OnChangeProps) => ProviderInputState;

export type PersistedProviderInputsContextType = {
  providerInputs: ProviderInputField;
  onProviderInputChange: OnProviderInputChange;
};

export const PersistedProviderInputsContext = React.createContext<
  PersistedProviderInputsContextType | undefined
>(undefined);

type ProviderInputsProviderArguments = {
  children: React.ReactNode;
  providerFields: ProviderField[];
  fixedValue?: string | undefined;
};

type OnChangeProps = {
  providerInputName: string;
  value: ProviderInputState;
};

export const PersistedProviderInputsProvider = ({
  providerFields,
  fixedValue,
  children,
}: ProviderInputsProviderArguments) => {
  const initialProviderInput = calculateProviderInputs(providerFields, fixedValue);
  const [providerInputs, setProviderInputs] = useState<ProviderInputField>(initialProviderInput);

  const onProviderInputChange = ({ providerInputName, value }: OnChangeProps) => {
    setProviderInputs((inputs) => ({
      ...inputs,
      [providerInputName]: value,
    }));

    return value;
  };

  return (
    <PersistedProviderInputsContext.Provider value={{ providerInputs, onProviderInputChange }}>
      {children}
    </PersistedProviderInputsContext.Provider>
  );
};

export const usePersistedProviderInputs = () => useContext(PersistedProviderInputsContext);
